import { createStore, createEvent, createEffect, sample } from 'effector';
import { activateQrCode } from './activationStore';
import { termsStore, submitTermsAgreementFx } from './termsStore';
import { campaignStore } from './campaignStore';
import { setMainButtonState, setMainButtonText, setMainButtonAction, MainButtonState, MainButtonStore, setMainButton } from './mainButtonStore';
import { $skipQrScan } from './skipQrScan';
import { AuthenticatedRequest, createAuthenticatedRequest } from './types';
import { createTelegramApiClient } from '../utils/apiClient';
import { ContactResponse } from '../types';

// Типы
export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

export interface UserCheckRequest {
  telegramId: string;
  username?: string;
}

export interface UserCheckResponse {
  exist: boolean;
  phone: boolean;
  username: boolean;
}

export interface UserError {
  message: string;
  isSessionExpired?: boolean;
}

export interface UpsertUserRequest {
  telegramId: string;
  username?: string;
  phone?: string;
  displayName?: string;
}

// События
export const setUser = createEvent<TelegramUser | null>();
export const setUserPhone = createEvent<string>();
export const setContactRequested = createEvent<boolean>();
export const setUserExists = createEvent<boolean>();
export const setUserHasPhone = createEvent<boolean>();
export const setTelegramInitData = createEvent<string>();
export const setIsTelegramAvailable = createEvent<boolean>();
export const activateMainButton = createEvent();
export const scanQRCode = createEvent();
export const phoneContactRequest = createEvent();
export const handleQRResult = createEvent<string>();
export const handlePhoneContactRequest = createEvent<string>();
export const setPendingQrCode = createEvent<string | null>();
export const activatePendingCode = createEvent();

// Эффекты
export const checkUserExistsFx = createEffect<AuthenticatedRequest<UserCheckRequest>, UserCheckResponse, UserError>(async ({ data, initData }) => {
  console.log('checkUserExistsFx started with data:', data);
  
  try {
    const telegramClient = createTelegramApiClient(initData);
    const result = await telegramClient.post('/activation/user/check', data, {
      loadingText: 'Проверяем пользователя...'
    });
    
    console.log('checkUserExistsFx result:', result);
    return result;
  } catch (error: any) {
    console.log('checkUserExistsFx error:', error);
    
    const isSessionExpired = error.message?.toLowerCase().includes('сеанс истек') ||
                            error.message?.toLowerCase().includes('session expired') ||
                            error.message?.toLowerCase().includes('401');
    
    console.log('checkUserExistsFx isSessionExpired:', isSessionExpired);
    
    const userError: UserError = {
      message: error.message || `Ошибка проверки пользователя`,
      isSessionExpired
    };
    throw userError;
  }
});

export const upsertUserFx = createEffect<AuthenticatedRequest<UpsertUserRequest>, { success: boolean }, UserError>(async ({ data, initData }) => {
  try {
    const telegramClient = createTelegramApiClient(initData);
    const result = await telegramClient.post('/activation/user/upsert', data, {
      loadingText: 'Актуализируем данные...'
    });
    
    return result;
  } catch (error: any) {
    const isSessionExpired = error.message?.toLowerCase().includes('сеанс истек') ||
                            error.message?.toLowerCase().includes('session expired') ||
                            error.message?.toLowerCase().includes('401');
    
    const userError: UserError = {
      message: error.message || `Ошибка сохранения пользователя`,
      isSessionExpired
    };
    throw userError;
  }
});

// Тип состояния
export interface UserState {
  user: TelegramUser | null;
  userPhone: string;
  contactRequested: boolean;
  userExists: boolean;
  userHasPhone: boolean;
  telegramInitData: string;
  isTelegramAvailable: boolean;
  isCheckingUser: boolean;
  isUpsertingUser: boolean;
  isSessionExpired: boolean;
  pendingQrCode: string | null;
}

// Store
export const userStore = createStore<UserState>({
  user: null,
  userPhone: '',
  contactRequested: false,
  userExists: false,
  userHasPhone: false,
  telegramInitData: '',
  isTelegramAvailable: false,
  isCheckingUser: false,
  isUpsertingUser: false,
  isSessionExpired: false,
  pendingQrCode: null,
})
  .on(setUser, (state, user) => ({ ...state, user }))
  .on(setUserPhone, (state, userPhone) => {
    const newState = {
      ...state,
      userPhone,
      userHasPhone: !!userPhone // Устанавливаем userHasPhone в true если есть телефон
    };
    console.log('setUserPhone called:', { userPhone, userHasPhone: newState.userHasPhone });
    return newState;
  })
  .on(setContactRequested, (state, contactRequested) => ({ ...state, contactRequested }))
  .on(setUserExists, (state, userExists) => ({ ...state, userExists }))
  .on(setUserHasPhone, (state, userHasPhone) => ({ ...state, userHasPhone }))
  .on(setTelegramInitData, (state, telegramInitData) => {
    return { ...state, telegramInitData };
  })
  .on(setIsTelegramAvailable, (state, isTelegramAvailable) => ({ ...state, isTelegramAvailable }))
  .on(activateMainButton, (state) => {
    console.log('activateMainButton event triggered');
    // Активируем MainButton с текстом "Сканировать QR-код"
    setMainButtonText('Сканировать QR-код');
    setMainButtonAction(() => {
      console.log('MainButton clicked via activateMainButton event');
      scanQRCode();
    });
    setMainButtonState('scan');
    console.log('MainButton activated with "Сканировать QR-код" text');
    return state;
  })
  .on(scanQRCode, (state) => {
    console.log('scanQRCode event triggered from userStore');   
    if (window.Telegram?.WebApp && window.Telegram?.WebApp?.showScanQrPopup) {
      window.Telegram.WebApp.showScanQrPopup({text: 'Отсканируйте один QR-код для активации'}, (result: string) => {
        window.Telegram?.WebApp?.closeScanQrPopup();       
        handleQRResult(result);
      });
    }
    return state;
  })
  .on(phoneContactRequest, (state) => {
    console.log('phoneContactRequest event triggered from userStore');
    if (window.Telegram?.WebApp && window.Telegram?.WebApp?.requestContact) {
    window.Telegram.WebApp.requestContact((success,response) => {
      if (success) {
        const contact = (response as ContactResponse).responseUnsafe.contact;        
        handlePhoneContactRequest(contact.phone_number);
      }
    })
  }
    return state;
  })
  .on(handleQRResult, (state, qrCode) => {
    console.log('handleQRResult called with qrCode:', qrCode);
    console.log('Current state.user:', state.user);
    if (!qrCode.startsWith('QC:')) {
      console.log('Invalid QR code format. Code should start with "QC:"');
      window.Telegram?.WebApp?.showAlert("Неверный формат QR-кода");
      return state;
    }

    console.log('Valid QR code format, storing QR code for activation...');
    // Сохраняем QR-код в состоянии для последующей активации
    return {
      ...state,
      pendingQrCode: qrCode
    };
  })
  .on(handlePhoneContactRequest, (state, phone) => {
    console.log('handlePhoneContactRequest called with phone:', phone);
    return {
      ...state,
      userPhone: phone,
      userHasPhone: !!phone,
    };
  })
  .on(setPendingQrCode, (state, pendingQrCode) => ({
    ...state,
    pendingQrCode
  }))
  .on(checkUserExistsFx.pending, (state, isCheckingUser) => ({ ...state, isCheckingUser }))
  .on(checkUserExistsFx.doneData, (state, result) => {
    const newState = {
      ...state,
      userExists: result.exist,
      userHasPhone: result.phone,
    };
    console.log('checkUserExistsFx.doneData called:', { 
      result, 
      userExists: newState.userExists,
      userHasPhone: newState.userHasPhone,
      hasUsername: !!state.user?.username
    });
    return newState;
  })
  .on(checkUserExistsFx.failData, (state, error) => {
    console.log('checkUserExistsFx failed with error:', error);
    console.log('isSessionExpired:', error.isSessionExpired);
    return {
      ...state,
      isSessionExpired: error.isSessionExpired || false
    };
  })
  .on(upsertUserFx.pending, (state, isUpsertingUser) => ({ ...state, isUpsertingUser }))
  .on(upsertUserFx.doneData, (state) => {
    const newState = { 
      ...state, 
      userExists: true,
      userHasPhone: !!state.userPhone // Устанавливаем userHasPhone на основе userPhone
    };
    console.log('upsertUserFx.doneData called:', { 
      userPhone: state.userPhone, 
      userHasPhone: newState.userHasPhone,
      userExists: newState.userExists,
      hasUsername: !!state.user?.username
    });
    return newState;
  })
  .on(upsertUserFx.failData, (state, error) => {
    console.log('upsertUserFx failed with error:', error);
    console.log('isSessionExpired:', error.isSessionExpired);
    return {
      ...state,
      isSessionExpired: error.isSessionExpired || false
    };
  });

// Actions
export const checkUserExists = createEvent();
export const upsertUser = createEvent<Omit<UpsertUserRequest, 'telegramId'>>();

// Добавляем логирование для отслеживания вызовов
checkUserExists.watch(() => {
  console.log('checkUserExists event triggered');
});

upsertUser.watch(() => {
  console.log('upsertUser event triggered');
});

// Логика
sample({
  clock: checkUserExists,
  source: userStore,
  filter: (state) => !!state.user?.id && !state.isCheckingUser,
  fn: (state) => createAuthenticatedRequest({
    telegramId: state.user!.id.toString(),
    username: state.user!.username
  }, state.telegramInitData),
  target: checkUserExistsFx
});

sample({
  clock: upsertUser,
  source: userStore,
  filter: (state) => !!state.user?.id && !state.isUpsertingUser,
  fn: (state, request) => createAuthenticatedRequest({
    telegramId: state.user!.id.toString(),
    ...request
  }, state.telegramInitData),
  target: upsertUserFx
});

// Автоматически проверяем существование пользователя после согласия с условиями
sample({
  clock: submitTermsAgreementFx.doneData,
  source: { user: userStore, terms: termsStore },
  filter: ({ user, terms }) => {
    const shouldCheck = terms.isAgreed && !!user.user?.id && !user.isCheckingUser && !user.userExists;
    console.log('Auto-checking user existence after terms agreement:', { 
      isAgreed: terms.isAgreed, 
      hasUser: !!user.user?.id, 
      isChecking: user.isCheckingUser, 
      userExists: user.userExists,
      shouldCheck 
    });
    return shouldCheck;
  },
  fn: ({ user, terms }) => {
    console.log('Auto-checking user existence after terms agreement');
    return createAuthenticatedRequest({
      telegramId: user.user!.id.toString(),
      username: user.user!.username
    }, user.telegramInitData);
  },
  target: checkUserExistsFx
});

// Активируем MainButton когда пользователь проверен и готов
sample({
  clock: [checkUserExistsFx.doneData, upsertUserFx.doneData],
  source: { user: userStore, terms: termsStore },
  filter: ({ user, terms }) => {
    const shouldActivate = terms.isAgreed && !terms.isVisible && user.userExists && (user.userHasPhone || !!user.user?.username);
    console.log('checkUserExistsFx/upsertUserFx.doneData filter:', { 
      isAgreed: terms.isAgreed, 
      isVisible: terms.isVisible,
      userExists: user.userExists, 
      userHasPhone: user.userHasPhone, 
      hasUsername: !!user.user?.username,
      shouldActivate 
    });
    return shouldActivate;
  },
  fn: () => {
    console.log('User is ready, activating MainButton for QR scanning...');
    const skipQrScan = $skipQrScan.getState();
    return skipQrScan ? 'Активировать код' : 'Сканировать QR-код';
  },
  target: setMainButtonText
});

// Устанавливаем действие для активной кнопки после проверки пользователя
sample({
  clock: [checkUserExistsFx.doneData, upsertUserFx.doneData],
  source: { user: userStore, terms: termsStore },
  filter: ({ user, terms }) => {
    const shouldActivate = terms.isAgreed && !terms.isVisible && user.userExists && (user.userHasPhone || !!user.user?.username);
    console.log('checkUserExistsFx/upsertUserFx.doneData action filter:', { 
      isAgreed: terms.isAgreed, 
      isVisible: terms.isVisible,
      userExists: user.userExists, 
      userHasPhone: user.userHasPhone, 
      hasUsername: !!user.user?.username,
      shouldActivate 
    });
    return shouldActivate;
  },
  fn: () => {
    const skipQrScan = $skipQrScan.getState();
    return () => {
      console.log('MainButton clicked - skipQrScan:', skipQrScan);
      skipQrScan ? activatePendingCode() : scanQRCode();
    };
  },
  target: setMainButtonAction
});
sample({
  clock: handlePhoneContactRequest,
  fn: () => true,
  target: setContactRequested
});

// Устанавливаем состояние для активной кнопки после проверки пользователя
sample({
  clock: [checkUserExistsFx.doneData, upsertUserFx.doneData, setContactRequested, submitTermsAgreementFx.doneData],
  source: { user: userStore, terms: termsStore, campaign: campaignStore },
  filter: ({ user, terms, campaign }) => {
    if (campaign.settings?.termsConfirm) {
      return false;
    }
    return terms.isAgreed && !terms.isVisible && user.userExists;
  },
  fn: ({user}, contactRequested) => {
    const skipQrScan = $skipQrScan.getState();
    let state: MainButtonState = 'scan_inactive';
    if (!user.userHasPhone &&  !user.contactRequested) {
      state = 'contact' as MainButtonState;
    }
    else if (user.userHasPhone || user.contactRequested === true) {
      state = 'scan' as MainButtonState;
    }
    const doAction = () => {
      if (state === ('contact' as MainButtonState)) return phoneContactRequest();
      return skipQrScan ? activatePendingCode() : scanQRCode();
    };
    const text = (state === ('contact' as MainButtonState))
      ? 'Отправить мой номер'
      : (skipQrScan ? 'Активировать код' : 'Сканировать QR-код');
    console.log('setMainButtonState called:', { state, text, skipQrScan });
    return {
      state: state,
      text: text,
      action: ()=> doAction()
    } as MainButtonStore;
  },
  target: setMainButton
});
/*
sample({
  clock: setContactRequested,
  filter: (flag) => flag === true,
  fn: () => {
    return 'scan' as const;
  },
  target: setMainButtonState
});
// Показываем неактивную кнопку "Сканировать QR-код" после согласия с условиями
sample({
  clock: submitTermsAgreementFx.doneData,
  source: termsStore,
  filter: (terms) => {
    const shouldShow = terms.isAgreed && !terms.isVisible;
    console.log('submitTermsAgreementFx.doneData filter:', { 
      isAgreed: terms.isAgreed, 
      isVisible: terms.isVisible, 
      shouldShow 
    });
    return shouldShow;
  },
  fn: () => {
    console.log('Terms agreed, showing inactive MainButton for QR scanning...');
    return 'Сканировать QR-код';
  },
  target: setMainButtonText
});

// Устанавливаем действие для неактивной кнопки после согласия (только если пользователь не готов)
sample({
  clock: submitTermsAgreementFx.doneData,
  source: { terms: termsStore, user: userStore },
  filter: ({ terms, user }) => {
    const shouldSetInactive = terms.isAgreed && !terms.isVisible && (!user.userExists || (!user.userHasPhone && !user.user?.username));
    console.log('submitTermsAgreementFx.doneData action filter:', { 
      isAgreed: terms.isAgreed, 
      isVisible: terms.isVisible,
      userExists: user.userExists,
      userHasPhone: user.userHasPhone,
      hasUsername: !!user.user?.username,
      shouldSetInactive 
    });
    return shouldSetInactive;
  },
  fn: () => {
    return () => {
      console.log('MainButton clicked but user not ready yet');
      // Показываем сообщение, что нужно дождаться проверки пользователя
      window.Telegram?.WebApp?.showAlert('Пожалуйста, подождите завершения проверки пользователя');
    };
  },
  target: setMainButtonAction
});

// Устанавливаем состояние для неактивной кнопки после согласия (только если пользователь не готов)
sample({
  clock: submitTermsAgreementFx.doneData,
  source: { terms: termsStore, user: userStore },
  filter: ({ terms, user }) => {
    const shouldSetInactive = terms.isAgreed && !terms.isVisible && (!user.userExists || (!user.userHasPhone && !user.user?.username));
    return shouldSetInactive;
  },
  fn: () => {
    return 'scan_inactive' as const;
  },
  target: setMainButtonState
});
*/



// Автоматически создаем пользователя если он не существует, но у него есть username
sample({
  clock: checkUserExistsFx.doneData,
  source: userStore,
  filter: (state, result) => {
    // Если пользователь не существует, но у него есть username, создаем его автоматически
    return !!(state.user?.username && (!result.exist || (result.exist && !result.username)));
  },
  fn: (state) => {
    console.log('User does not exist but has username, creating user automatically');
    return {
      username: state.user!.username,
      phone: state.userPhone,
      displayName: `${state.user!.first_name} ${state.user!.last_name || ''}`.trim()
    };
  },
  target: upsertUser
});
sample({
  clock: handlePhoneContactRequest,
  source: userStore,
  filter: (state, result) => {
    
    // Если пользователь не существует, но у него есть username, создаем его автоматически
    return state.contactRequested && !state.userHasPhone;
  },
  fn: (state) => {
    console.log('Updating user automatically', state);
    return {
      username: state.user!.username,
      phone: state.userPhone,
      displayName: `${state.user!.first_name} ${state.user!.last_name || ''}`.trim()
    };
  },
  target: upsertUser
});
// Активируем QR-код через activationStore
sample({
  clock: handleQRResult,
  source: userStore,
  filter: (state, qrCode) => qrCode.startsWith('QC:') && !!state.user,
  fn: (state, qrCode) => {
    const userPhone = state.userPhone;
    const firstName = state.user!.first_name || 'Unknown';
    const lastName = state.user!.last_name || '';

    const activationData = {
      code: qrCode,
      telegramId: state.user!.id.toString(),
      username: state.user!.username || 'unknown',
      phone: userPhone,
      displayName: `${firstName} ${lastName}`.trim() || 'Unknown User',
      initData: state.telegramInitData
    };

    console.log('Prepared activation data:', activationData);
    return activationData;
  },
  target: activateQrCode
});

sample({
  clock: activatePendingCode,
  source: userStore,
  filter: (state) => !!state.pendingQrCode && !!state.user,
  fn: (state) => {
    const userPhone = state.userPhone;
    const firstName = state.user!.first_name || 'Unknown';
    const lastName = state.user!.last_name || '';

    return {
      code: state.pendingQrCode!,
      telegramId: state.user!.id.toString(),
      username: state.user!.username || 'unknown',
      phone: userPhone,
      displayName: `${firstName} ${lastName}`.trim() || 'Unknown User',
      initData: state.telegramInitData
    };
  },
  target: activateQrCode
});

